import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';
import moment from 'moment';
import _ from 'lodash';

import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { getInvoicePDF, getOrderDetails, getOrderList, getPIMItemInfo, processPartsOrder, saveManageOrdersListBackup, saveOrderDetails } from '../../../redux/actions';
import { PNET_APP_QUERY_PARAM, sealCodeToCarrier, carrierCodeToTrackingURL } from '../../common/partsNetConstants';
import PartsNetOrderCancellationDialog from '../../dialogs/partsNetOrderCancellationDialog';
import LineStatusDescriptionsOverlayDialog from '../../dialogs/lineStatusDescriptionsOverlayDialog';

const CANCELLABLE_LINE_STATUSES = ['Awaiting Shipping', 'Backordered', 'Internal Req Requested', 'Internal Req Open', 'Internal Req Partial'];

const PartsNetOrderDetails = props => {
	const { order, user } = useSelector(({ order, user }) => ({ order, user }));
	const dispatch = useDispatch();

	const [orderDetails, setOrderDetails] = useState(order.orderDetails.responseObject);
	const { P_OUT_HEADER_REC, P_OUT_HEADER_VAL_REC, P_OUT_LINE_TBL, P_DELIVERY_STATUS_INFO_TBL } = orderDetails;
	const orderSource = P_OUT_HEADER_VAL_REC && P_OUT_HEADER_VAL_REC.ORDER_SOURCE;

	const [messageData, setMessageData] = useState();
	const [selectedLineIds, setSelectedLineIds] = useState([]);

	const [dialog, setDialog] = useState();

	useEffect(() => {
		if (!order.orderDetails) {
			browserHistory.push('/manageorders');
		} else {
			window.scrollTo(0, 0);

			dispatch(
				getPIMItemInfo(
					P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.map(item => item.ORDERED_ITEM),
					'requestType=getPIMItemInfo',
					pimResponse => {
						if (pimResponse.status) {
							setOrderDetails(orderDetails => ({
								...orderDetails,
								P_OUT_LINE_TBL: {
									...P_OUT_LINE_TBL,
									P_OUT_LINE_TBL_ITEMS: P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.map(item => ({
										...item,
										ORDERED_ITEM_DESCRIPTION: (pimResponse.responseObject.find(pimItem => pimItem.item_number === item.ORDERED_ITEM) || {}).item_description
									}))
								}
							}));
						}
					}
				)
			);
		}
	}, []);

	useUpdateEffect(() => {
		if (order.orderDetails && order.orderDetails.responseObject) {
			setOrderDetails(orderDetails => ({
				...order.orderDetails.responseObject,
				P_OUT_LINE_TBL: {
					...P_OUT_LINE_TBL,
					P_OUT_LINE_TBL_ITEMS: order.orderDetails.responseObject.P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.map(lineItem => {
						const itemDescription = (
							orderDetails.P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.find(previousLineItem => previousLineItem.ORDERED_ITEM === lineItem.ORDERED_ITEM) || {}
						).ORDERED_ITEM_DESCRIPTION;

						return {
							...lineItem,
							ORDERED_ITEM_DESCRIPTION: itemDescription
						};
					})
				}
			}));
		}
	}, [order.orderDetails]);

	const accessData = useMemo(
		() => ({
			editOrders: props.isResponsibilityExists('Edit Orders')
		}),
		[]
	);

	const { totalQuantity, subTotal, tax, isOrderCancellable } = useMemo(() => {
		const cancellableOrEnteredLineExists =
			P_OUT_LINE_TBL &&
			P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS &&
			P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.some(
				line => (line.FORM_FLOW_STATUS_CODE && line.FORM_FLOW_STATUS_CODE.toUpperCase() === 'ENTERED') || CANCELLABLE_LINE_STATUSES.includes(line.FORM_FLOW_STATUS_CODE)
			);

		return (
			(P_OUT_LINE_TBL &&
				P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS &&
				P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.reduce(
					(acc, cur) => ({
						totalQuantity: +acc.totalQuantity + +cur.ORDERED_QUANTITY,
						subTotal: acc.subTotal + cur.ORDERED_QUANTITY * cur.UNIT_SELLING_PRICE,
						tax: acc.tax + +cur.TAX_VALUE,
						isOrderCancellable:
							acc.isOrderCancellable &&
							cancellableOrEnteredLineExists &&
							(props.getItemTypeByName(cur.ORDERED_ITEM) !== 'S' &&
							cur.FORM_FLOW_STATUS_CODE &&
							!['ENTERED', 'CANCELLED'].includes(cur.FORM_FLOW_STATUS_CODE.toUpperCase()) &&
							!CANCELLABLE_LINE_STATUSES.includes(cur.FORM_FLOW_STATUS_CODE)
								? false
								: true)
					}),
					{
						totalQuantity: 0,
						subTotal: 0,
						tax: 0,
						isOrderCancellable: true
					}
				)) ||
			{}
		);
	}, [orderDetails]);

	const allLinesSelected = useMemo(
		() =>
			selectedLineIds.length &&
			P_OUT_LINE_TBL &&
			P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS &&
			P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.filter(item => props.getItemTypeByName(item.ORDERED_ITEM) !== 'S' && CANCELLABLE_LINE_STATUSES.includes(item.FORM_FLOW_STATUS_CODE))
				.length === selectedLineIds.length,
		[orderDetails, selectedLineIds]
	);

	const getInvoiceDocument = (invoiceNumber, e) => {
		e && e.preventDefault();
		dispatch(
			getInvoicePDF(`requestType=getInvoicePDF&invoiceNumber=${invoiceNumber}&accountNumber=${props.getSelectedAccountNumber()}`, response => {
				if (response.status) {
					if (response.responseObject) {
						window.open(response.responseObject);
					}
				} else {
					browserHistory.push('/error');
				}
			})
		);
	};

	const handleLineSelection = lineId => {
		setSelectedLineIds(selectedLineIds => {
			const newSelectedLineIds = selectedLineIds.filter(selectedLineId => selectedLineId !== lineId);

			return newSelectedLineIds.length !== selectedLineIds.length ? newSelectedLineIds : selectedLineIds.concat(lineId);
		});
	};

	const toggleSelectAll = () => {
		if (allLinesSelected) {
			setSelectedLineIds([]);
		} else {
			if (P_OUT_LINE_TBL && P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS) {
				setSelectedLineIds(P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.filter(item => CANCELLABLE_LINE_STATUSES.includes(item.FORM_FLOW_STATUS_CODE)).map(item => item.LINE_ID));
			}
		}
	};

	const closeDialog = id => {
		setDialog(null);
	};

	const processCancellation = payload => {
		setMessageData(null);

		dispatch(
			processPartsOrder(
				payload,
				`requestType=cancelOrder&appName=${orderDetails.P_OUT_HEADER_VAL_REC.ORDER_SOURCE === 'NCRP' ? 'NCRP' : 'PNET'}`,
				true,
				cancellationResponse => {
					if (cancellationResponse.status) {
						if (cancellationResponse.responseObject && cancellationResponse.responseObject.X_RETURN_STATUS === 'S') {
							setSelectedLineIds([]);

							dispatch(
								getOrderDetails(
									`requestType=getOrderDetails&P_ORDER_NUMBER=${orderDetails.P_OUT_HEADER_REC.ORDER_NUMBER}&P_PO_NUMBER=&ORIG_SYS_DOCUMENT_REF=&P_INVOICE=&${PNET_APP_QUERY_PARAM}`,
									orderDetailsResponse => {
										if (orderDetailsResponse.responseObject) {
											dispatch(saveOrderDetails(orderDetailsResponse));
										}
									},
									false,
									true
								)
							);

							dispatch(
								getOrderList(
									`accountNumber=${
										orderDetails.P_OUT_HEADER_VAL_REC.CUSTOMER_NUMBER
									}&requestType=manageOrders&orderNumber=${orderDetails.P_OUT_HEADER_REC.ORDER_NUMBER.trim()}&poNumber=&referenceNumber=&invoiceNumber=`,
									orderListResponse => {
										if (orderListResponse.status && orderListResponse.responseObject && orderListResponse.responseObject.length) {
											const manageOrdersListBackup = JSON.parse(JSON.stringify(order.manageOrdersListBackup));

											if (orderListResponse.responseObject[0].orderDetail && orderListResponse.responseObject[0].orderDetail.orderLine) {
												orderListResponse.responseObject[0].orderDetail.orderLine = orderListResponse.responseObject[0].orderDetail.orderLine.filter(
													lineItem => props.getItemTypeByName(lineItem.item) !== 'S'
												);
											}

											manageOrdersListBackup.ordersListResponse.responseObject = manageOrdersListBackup.ordersListResponse.responseObject.map(orderListItem =>
												orderListItem.orderHeader.orderNumber.toString() === orderDetails.P_OUT_HEADER_REC.ORDER_NUMBER.toString()
													? { ...orderListItem, ...orderListResponse.responseObject[0] }
													: orderListItem
											);

											dispatch(saveManageOrdersListBackup(manageOrdersListBackup));
										}
									}
								)
							);
						} else {
							if (
								cancellationResponse.responseObject &&
								!cancellationResponse.responseObject.X_RETURN_STATUS &&
								cancellationResponse.responseObject.message &&
								cancellationResponse.responseObject.message.toLowerCase().includes('timeout exceeded')
							) {
								setMessageData({
									message: 'Cancellation in progress, please check back after some time.'
								});
							}
						}
					} else {
						setMessageData({
							message: 'Something went wrong.',
							isError: true
						});
					}
				}
			)
		);
	};

	const handleLineCancellation = ({ cancellationReason, cancellationComments }) => {
		closeDialog('lineCancellationDialog');

		const orderLineCancellationPayload = {
			P_OPERATION: 'UPDATE',
			P_OPERATING_UNIT: 'GE US Appliances',
			RESPONSIBILITY: 'GEOM_SETUP _US_APL',
			RESP_APPLICATION: 'GEONT',
			SECURITY_GROUP: 'STANDARD',
			NLS_LANGUAGE: 'AMERICAN',
			P_LINE_TBL: {
				P_LINE_TBL_ITEM: P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.filter(lineItem => selectedLineIds.includes(lineItem.LINE_ID)).map(lineItem => ({
					CANCELLED_FLAG: 'Y',
					// CANCELLED_QUANTITY: '',
					ORDERED_QUANTITY: 0,
					HEADER_ID: lineItem.HEADER_ID,
					LINE_ID: lineItem.LINE_ID,
					OPERATION: 'UPDATE',
					CHANGE_REASON: cancellationReason,
					CHANGE_COMMENTS: cancellationComments.trim()
						? `${orderSource === 'NCRP' ? 'NCRP' : 'PNET'} - ${user.userDetails.firstName} ${user.userDetails.lastName} - ${cancellationComments.trim()}`
						: ''
				}))
			}
		};

		processCancellation(orderLineCancellationPayload);
	};

	const validateLines = e => {
		e.preventDefault();

		if (!selectedLineIds.length) {
			return;
		}

		setDialog({
			id: 'lineCancellationDialog',
			title: 'CANCEL',
			content: 'Are you sure you want to cancel the selected order lines?',
			buttons: ['no', 'yes'],
			onConfirm: handleLineCancellation,
			onCancel: closeDialog
		});
	};

	const handleOrderCancellation = ({ cancellationReason, cancellationComments }) => {
		closeDialog('headerCancellationDialog');

		const accountNumber = props.getSelectedAccountNumber();

		const orderHeaderCancellationPayload = {
			P_OPERATION: 'UPDATE',
			P_OPERATING_UNIT: 'GE US Appliances',
			RESPONSIBILITY: 'GEOM_SETUP _US_APL',
			RESP_APPLICATION: 'GEONT',
			SECURITY_GROUP: 'STANDARD',
			NLS_LANGUAGE: 'AMERICAN',
			P_HEADER_REC: {
				CANCELLED_FLAG: 'Y',
				HEADER_ID: orderDetails.P_OUT_HEADER_REC.HEADER_ID,
				ORDER_NUMBER: orderDetails.P_OUT_HEADER_REC.ORDER_NUMBER,
				OPERATION: 'UPDATE',
				CHANGE_REASON: cancellationReason,
				CHANGE_COMMENTS: cancellationComments.trim()
					? `${orderSource === 'NCRP' ? 'NCRP' : 'PNET'} - ${user.userDetails.firstName} ${user.userDetails.lastName} - ${cancellationComments.trim()}`
					: ''
			},
			P_HEADER_VAL_REC: {
				CUSTOMER_NUMBER: accountNumber,
				ORDER_SOURCE: orderDetails.P_OUT_HEADER_VAL_REC.ORDER_SOURCE
			}
		};

		processCancellation(orderHeaderCancellationPayload);
	};

	const validateOrder = e => {
		e.preventDefault();

		if (!isOrderCancellable) {
			return;
		}

		setDialog({
			id: 'headerCancellationDialog',
			title: 'CANCEL',
			content: 'Are you sure you want to cancel this order?',
			buttons: ['no', 'yes'],
			onConfirm: handleOrderCancellation,
			onCancel: closeDialog
		});
	};

	const handleGenericDialog = (id, e) => {
		e && e.preventDefault();

		switch (id) {
			case 'lineStatusDescriptionDialog':
				setDialog({
					id,
					onClose: closeDialog
				});
				break;
			default:
		}
	};

	const orderDeliveryStatusByLine = useMemo(() => {
		return (
			P_DELIVERY_STATUS_INFO_TBL.P_DELIVERY_STATUS_INFO_TBL_ITEMS &&
			P_DELIVERY_STATUS_INFO_TBL.P_DELIVERY_STATUS_INFO_TBL_ITEMS.reduce((acc, item) => {
				acc[item.LINE_NUMBER] = {
					sealCode: item.SEAL_CODE,
					trackingNumber: item.TRACKING_NUMBER
				};
				return acc;
			}, {})
		);
	}, [P_DELIVERY_STATUS_INFO_TBL]);

	const lineTableItems = useMemo(() => {
		if (orderDetails && orderDetails.P_OUT_HEADER_VAL_REC && ['EDI', 'EDI-Parts'].includes(orderDetails.P_OUT_HEADER_VAL_REC.ORDER_SOURCE)) {
			return (
				orderDetails &&
				orderDetails.P_OUT_LINE_TBL &&
				orderDetails.P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS &&
				orderDetails.P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS.toSorted((a, b) => {
					if (a.CUSTOMER_LINE_NUMBER === null) {
						return 1;
					}
					if (b.CUSTOMER_LINE_NUMBER === null) {
						return -1;
					}
					return a.CUSTOMER_LINE_NUMBER - b.CUSTOMER_LINE_NUMBER;
				})
			);
		} else {
			return orderDetails && orderDetails.P_OUT_LINE_TBL && orderDetails.P_OUT_LINE_TBL.P_OUT_LINE_TBL_ITEMS;
		}
	}, [orderDetails]);

	return orderDetails ? (
		<div className="pNetOrderDetails">
			<div className="push">
				<h1 className="pageTitle">Order Details</h1>
			</div>
			{messageData && (
				<p className="bold" style={{ color: messageData.isError ? '#ff0000' : '#ff9500' }}>
					{messageData.message}
				</p>
			)}
			<div className="pNetOrderDetailsHeader push">
				<div>
					{orderDetails && orderDetails.P_OUT_HEADER_REC.ORDER_NUMBER && <h2>CSO (Order number): {orderDetails && orderDetails.P_OUT_HEADER_REC.ORDER_NUMBER}</h2>}
					{orderDetails && orderDetails.P_OUT_HEADER_REC.ORIG_SYS_DOCUMENT_REF && <h3>Order Reference #: {orderDetails.P_OUT_HEADER_REC.ORIG_SYS_DOCUMENT_REF}</h3>}
					{orderDetails && orderDetails.P_OUT_HEADER_VAL_REC.CUSTOMER_NUMBER && <h3>Account #: {orderDetails && orderDetails.P_OUT_HEADER_VAL_REC.CUSTOMER_NUMBER}</h3>}
				</div>
				{accessData.editOrders && (
					<p className="pNetOrderDetailsHeaderItems">
						<button disabled={!selectedLineIds.length} onClick={validateLines}>
							CANCEL SELECTED LINES
						</button>
						<button disabled={!isOrderCancellable} onClick={validateOrder}>
							CANCEL ORDER
						</button>
					</p>
				)}
			</div>
			<div className="push orderReviewColumns columns threeColumns">
				<div className="orderReviewColumn column column1">
					<p>
						<strong>Status:</strong> {P_OUT_HEADER_REC.FLOW_STATUS_CODE}
						<br />
						<strong>Order Date:</strong> {P_OUT_HEADER_REC.ORDERED_DATE && props.formatDate(P_OUT_HEADER_REC.ORDERED_DATE, 'MM/DD/YY')}
						<br />
						{P_OUT_HEADER_REC.ATTRIBUTE2 && (
							<span>
								<strong>Order started by:</strong> {P_OUT_HEADER_REC.ATTRIBUTE2}
								<br />
							</span>
						)}
					</p>
					{P_OUT_HEADER_REC.REQUEST_DATE && (
						<p>
							<strong>Requested Ship Date:</strong> {P_OUT_HEADER_REC.REQUEST_DATE && props.formatDate(P_OUT_HEADER_REC.REQUEST_DATE, 'MM/DD/YY')}
						</p>
					)}
				</div>
				<div className="orderReviewColumn column column2">
					<p>
						<strong>Customer P.O.:</strong> {P_OUT_HEADER_REC.CUST_PO_NUMBER}
						<br />
						<strong>Order Reference:</strong> {P_OUT_HEADER_REC.ORIG_SYS_DOCUMENT_REF}
						<br />
						{orderDetails.P_OUT_HEADER_VAL_REC.SHIPPING_METHOD && (
							<span>
								<strong>Shipping Method:</strong> {P_OUT_HEADER_VAL_REC.SHIPPING_METHOD}
								<br />
							</span>
						)}
						<strong>Order Type:</strong>{' '}
						{P_OUT_HEADER_REC.SHIPMENT_PRIORITY_CODE &&
							(P_OUT_HEADER_REC.SHIPMENT_PRIORITY_CODE[0] === 'R' ? 'Regular' : P_OUT_HEADER_REC.SHIPMENT_PRIORITY_CODE[0] === 'E' ? 'Emergency' : '')}
						<br />
					</p>
					<p>
						{P_OUT_HEADER_REC.ATTRIBUTE8 && (
							<span>
								<strong>Delivery Phone:</strong> {props.normalizePhoneNumber(P_OUT_HEADER_REC.ATTRIBUTE8, props.isInternationalPartsCustomer)}
								<br />
							</span>
						)}
					</p>
				</div>
				<div className="orderReviewColumn column column3">
					{P_OUT_HEADER_VAL_REC.SHIP_TO_ADDRESS1 && (
						<p>
							<strong>Ship to:</strong>
							<br />
							{P_OUT_HEADER_VAL_REC.SHIP_TO_CUSTOMER_NAME && (
								<span>
									{P_OUT_HEADER_VAL_REC.SHIP_TO_CUSTOMER_NAME}
									<br />
								</span>
							)}
							<span>
								{P_OUT_HEADER_VAL_REC.SHIP_TO_ADDRESS1}
								<br />
							</span>
							{P_OUT_HEADER_VAL_REC.SHIP_TO_ADDRESS2 && (
								<span>
									{P_OUT_HEADER_VAL_REC.SHIP_TO_ADDRESS2}
									<br />
								</span>
							)}
							{P_OUT_HEADER_VAL_REC.SHIP_TO_ADDRESS3 && (
								<span>
									{P_OUT_HEADER_VAL_REC.SHIP_TO_ADDRESS3}
									<br />
								</span>
							)}
							<span>
								{P_OUT_HEADER_VAL_REC.SHIP_TO_CITY +
									', ' +
									P_OUT_HEADER_VAL_REC.SHIP_TO_STATE +
									', ' +
									P_OUT_HEADER_VAL_REC.SHIP_TO_COUNTRY +
									', ' +
									P_OUT_HEADER_VAL_REC.SHIP_TO_ZIP}
								<br />
							</span>
						</p>
					)}
					{P_OUT_HEADER_VAL_REC.INVOICE_TO_ADDRESS1 && (
						<p>
							<strong>Bill to:</strong>
							<br />
							{P_OUT_HEADER_VAL_REC.INVOICE_TO_CUSTOMER_NAME && (
								<span>
									{P_OUT_HEADER_VAL_REC.INVOICE_TO_CUSTOMER_NAME}
									<br />
								</span>
							)}
							<span>
								{P_OUT_HEADER_VAL_REC.INVOICE_TO_ADDRESS1}
								<br />
							</span>
							{P_OUT_HEADER_VAL_REC.INVOICE_TO_ADDRESS2 && (
								<span>
									{P_OUT_HEADER_VAL_REC.INVOICE_TO_ADDRESS2}
									<br />
								</span>
							)}
							<span>
								{P_OUT_HEADER_VAL_REC.INVOICE_TO_CITY +
									', ' +
									P_OUT_HEADER_VAL_REC.INVOICE_TO_STATE +
									', ' +
									P_OUT_HEADER_VAL_REC.INVOICE_TO_COUNTRY +
									', ' +
									P_OUT_HEADER_VAL_REC.INVOICE_TO_ZIP}
								<br />
							</span>
						</p>
					)}
				</div>
			</div>
			<div className="pNetOrderDetailsTableWrapper">
				<table className="pNetOrderDetailsTable orderReviewTable dataTableType2 noRowBorders milktoast">
					<thead>
						<tr>
							<th className="lineSelector">
								<label htmlFor="select-all-toggle" style={{ display: 'block', width: 'max-content' }}>
									<input id="select-all-toggle" type="checkbox" checked={allLinesSelected} onChange={toggleSelectAll} disabled={!accessData.editOrders} />
									All
								</label>
							</th>
							<th className="priceSource">Sales Agreement</th>
							<th>Item</th>
							<th className="text-center">Original Item</th>
							<th className="itemDescription">Item Description</th>
							<th className="quantity text-center">Order Qty</th>
							{orderSource !== 'NCRP' && <th className="text-right">Price</th>}
							<th className="text-center">Shipped Qty</th>
							<th className="lineStatus text-center">
								Line Status{' '}
								<a className="questionMarkIcon" href="" onClick={handleGenericDialog.bind(this, 'lineStatusDescriptionDialog')}>
									<img
										src="../images/icon-question-mark.png"
										width={12}
										height={11}
										alt="What is this?"
										title="What is this?"
										style={{ filter: 'brightness(1.2)' }}
									/>
								</a>
							</th>
							<th>Carrier Code</th>
							<th>Tracking Number</th>
							<th>Customer Reference</th>
							{orderSource !== 'NCRP' && <th>Invoice Number</th>}
						</tr>
						<tr>
							<th className="toast" colSpan={12}></th>
						</tr>
					</thead>
					<tbody>
						{lineTableItems &&
							lineTableItems.map((item, index) => {
								const invoiceNumber =
									item.INVOICE_DETAILS &&
									item.INVOICE_DETAILS.INVOICE_DETAILS_ITEMS &&
									item.INVOICE_DETAILS.INVOICE_DETAILS_ITEMS[0] &&
									item.INVOICE_DETAILS.INVOICE_DETAILS_ITEMS[0].INVOICE_NUMBER;

								const lineStatus = props.getLineStatusFromMappingForParts(item, true);
								const { sealCode, trackingNumber } = orderDeliveryStatusByLine[item.LINE_NUMBER] || {};
								let trackingURL, carrierCodeSeparatorIndex;

								if (sealCode && trackingNumber && (carrierCodeSeparatorIndex = sealCode.indexOf('-'))) {
									let carrierCode = sealCode.slice(carrierCodeSeparatorIndex + 1);
									carrierCode = carrierCode && carrierCode.split(' ')[0];
									trackingURL =
										carrierCode && carrierCodeToTrackingURL[carrierCode] && carrierCodeToTrackingURL[carrierCode].replace('<TRACKING_NUMBER>', trackingNumber);
								}

								return (
									<tr key={index}>
										<td>
											<input
												type="checkbox"
												checked={selectedLineIds.includes(item.LINE_ID)}
												onChange={handleLineSelection.bind(this, item.LINE_ID)}
												disabled={!accessData.editOrders || !CANCELLABLE_LINE_STATUSES.includes(item.FORM_FLOW_STATUS_CODE)}
											/>
										</td>
										<td className="priceSource">{P_OUT_HEADER_VAL_REC.BLANKET_AGREEMENT_NAME}</td>
										<td>{item.ORDERED_ITEM}</td>
										<td>{item.ATTRIBUTE16}</td>
										<td className="itemDescription">{item.ORDERED_ITEM_DESCRIPTION}</td>
										<td className="text-center">{item.ORDERED_QUANTITY}</td>
										{orderSource !== 'NCRP' && (
											<td className="text-right">{item.UNIT_SELLING_PRICE && props.formatMoney(item.UNIT_SELLING_PRICE, 0, '.', ',', false, '$')}</td>
										)}
										<td className="text-center">{item.SHIPPED_QUANTITY || 0}</td>
										<td className="lineStatus text-center">
											{lineStatus}
											{lineStatus.toUpperCase() === 'BACKORDERED' && item.SCHEDULE_SHIP_DATE && (
												<React.Fragment>
													<br />
													<small className="block bold" style={{ whiteSpace: 'nowrap', marginTop: '.25rem' }}>
														ETA: {moment(item.SCHEDULE_SHIP_DATE).format('MM/DD/YYYY')}
													</small>
												</React.Fragment>
											)}
										</td>
										<td>{sealCodeToCarrier[sealCode]}</td>
										<td>
											{trackingURL ? (
												<a href={trackingURL} target="_blank">
													{trackingNumber}
												</a>
											) : (
												trackingNumber
											)}
										</td>
										<td className="customerReference">{item.ATTRIBUTE4}</td>
										{orderSource !== 'NCRP' && (
											<td>
												<a href="" onClick={getInvoiceDocument.bind(this, invoiceNumber)}>
													{invoiceNumber}
												</a>
											</td>
										)}
									</tr>
								);
							})}
					</tbody>
					<tfoot>
						<tr>
							<td colSpan={5}>Total Items</td>
							<td className="text-center">{totalQuantity}</td>
							<td colSpan={7}></td>
						</tr>
					</tfoot>
				</table>
			</div>
			{orderSource !== 'NCRP' && (
				<table className="subtotalTable orderReviewSubtotalTable" style={{ width: 'min(100%, 20rem)' }}>
					<tbody>
						<tr className="subtotal">
							<td>Subtotal:</td>
							<td className="price" style={{ textAlign: 'right' }}>
								{props.formatMoney(subTotal.toFixed(2), 0, '.', ',', false, '$')}
							</td>
						</tr>
						{orderDetails.tariffSurcharge && (
							<tr className="tax">
								<td>Tariff Surcharge:</td>
								<td className="price" style={{ textAlign: 'right' }}>
									{props.formatMoney(orderDetails.tariffSurcharge, 0, '.', ',', false, '$')}
								</td>
							</tr>
						)}
						<tr className="tax">
							<td>Order Tax:</td>
							<td className="price" style={{ textAlign: 'right' }}>
								{props.formatMoney(tax.toFixed(2), 0, '.', ',', false, '$')}
							</td>
						</tr>
						<tr className="total">
							<td>Order Total:</td>
							<td className="price" style={{ textAlign: 'right' }}>
								{props.formatMoney((subTotal + tax).toFixed(2), 0, '.', ',', false, '$')}
							</td>
						</tr>
					</tbody>
				</table>
			)}
			{dialog && ['lineCancellationDialog', 'headerCancellationDialog'].includes(dialog.id) && (
				<PartsNetOrderCancellationDialog type={dialog.id === 'lineCancellationDialog' ? 'LINE' : 'HEADER'} onConfirm={dialog.onConfirm} onCancel={dialog.onCancel} />
			)}
			{dialog && dialog.id === 'lineStatusDescriptionDialog' && <LineStatusDescriptionsOverlayDialog handleDialog={dialog.onClose} showDialog={true} isPNETOrNCRP />}
		</div>
	) : null;
};

export default PartsNetOrderDetails;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/Order/partsNetOrderDetails.jsx